import { backend_url } from "@/constants";
import { AXIOS_CONFIG } from "@/utils/api";
import axios from "axios";
import type {
  ClientProfileResponse,
  DocumentUserType,
  UpdatePayload,
} from "./types";
import type { Job } from "@/types";
import { Endpoints } from "@api/endpoints.ts";
import { ClientProfileEndpoints } from "@smartwiz/shared-types";

const CLIENT_PROFILE_URL = `${backend_url}/${ClientProfileEndpoints.profile}`;
const DYNAMIC_ITEMS_URL = `${backend_url}/${ClientProfileEndpoints.dynamicItems}`;
const OCR_JOB_URL = `${backend_url}/${ClientProfileEndpoints.startOCR}`;
const DELETE_DOC_URL = `${backend_url}/${ClientProfileEndpoints.archiveDocument}`;
const RECATEGORIZE_DOC_URL = `${backend_url}/${ClientProfileEndpoints.recategorizeDocument}`;
const EXTRACTION_TOGGLE_URL = `${backend_url}/${ClientProfileEndpoints.extractionToggle}`;
const FORM_DATA_URL = `${backend_url}/${ClientProfileEndpoints.formData}`;
const UPLOAD_DOC_URL = `${backend_url}/${Endpoints.documents}`;

/**
 * Fetches client profile data from the backend API.
 *
 * @param {string} userId - The ID of the user whose profile is being requested.
 * @param {number} [taxYear] - Optional tax year for which the profile data is requested.
 *                            If not provided, the backend defaults to the previous year.
 * @param {boolean} [includeEngagementLetter=true] - Optional flag to include engagement letter documents in the response.
 *                                                   Defaults to true.
 * @returns {Promise<ClientProfileResponse>} A promise that resolves to the client profile data.
 * @throws Will throw an error if the request fails.
 */
export const getClientProfile = async (
  userId: string,
  taxYear?: number,
  includeEngagementLetter: boolean = true,
  includeArchived: boolean = false
): Promise<ClientProfileResponse> => {
  try {
    const params: Record<string, any> = {};
    if (taxYear !== undefined) {
      params.tax_year = taxYear;
    }
    params.include_engagement_letter = includeEngagementLetter;
    params.slim = true;
    if (includeArchived) {
      params.include_archived = true;
    }

    const response = await axios.get(`${CLIENT_PROFILE_URL}${userId}/`, {
      ...AXIOS_CONFIG,
      params,
    });

    if (response.data.message === "Client profile successfully retrieved") {
      return {
        message: response.data.message,
        content: response.data.content,
      };
    } else {
      throw new Error(
        response.data.message ?? "Failed to fetch client profile"
      );
    }
  } catch (error) {
    console.error("Error fetching client profile:", error);
    throw error;
  }
};

/**
 * Performs an update on the client profile.
 * @param userId ID of user performing the update.
 * @param payload The key-value used to identify field to update and the value to update it to.
 * @param taxYear REQUIRED tax year to update. Must always be explicitly provided for compliance.
 */
export const updateProfileField = async (
  userId: string,
  payload: UpdatePayload,
  taxYear: number
): Promise<{ message: string; content: any }> => {
  try {
    const response = await axios.post(
      `${CLIENT_PROFILE_URL}${userId}/`,
      payload,
      {
        ...AXIOS_CONFIG,
        params: { tax_year: taxYear },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating profile:", error);
    throw error;
  }
};

/**
 * Add an item to a dynamic list on the client profile.
 * @param userId User adding item.
 * @param payload The key of the item to add a new element to.
 * @param taxYear REQUIRED tax year to update. Must always be explicitly provided for compliance.
 */
export const addDynamicItem = async (
  userId: string,
  payload: Pick<UpdatePayload, "key">,
  taxYear: number
): Promise<{ message: string; content: any }> => {
  try {
    const response = await axios.post(
      `${DYNAMIC_ITEMS_URL}${userId}/`,
      payload,
      {
        ...AXIOS_CONFIG,
        params: { tax_year: taxYear },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating dynamic items:", error);
    throw error;
  }
};

/**
 * Deletes a specific item in a list in the client profile fields.
 * @param userId ID of user deleting the item.
 * @param key The key of the item to delete.
 * @param taxYear REQUIRED tax year to update. Must always be explicitly provided for compliance.
 */
export const deleteDynamicItems = async (
  userId: string,
  key: string,
  taxYear: number
): Promise<{ message: string; content: any }> => {
  try {
    const response = await axios.delete(`${DYNAMIC_ITEMS_URL}${userId}/`, {
      ...AXIOS_CONFIG,
      params: { tax_year: taxYear },
      data: { key },
    });
    return response.data;
  } catch (error) {
    console.error("Error deleting dynamic item:", error);
    throw error;
  }
};

export const startOCRJob = async (userID: string): Promise<Job> => {
  try {
    const response = await axios.post(
      `${OCR_JOB_URL}`,
      { clientId: userID },
      {
        ...AXIOS_CONFIG,
      }
    );
    return response.data.content?.job;
  } catch (error) {
    console.error("Error starting OCR job:", error);
    throw error;
  }
};

/**
 * Deletes (archives) a document form by ID.
 *
 * @param {string} formId - The ID of the form to delete.
 * @returns {Promise<{ message: string }>} A promise resolving to a success message.
 * @throws {Error} Will throw if the deletion fails.
 */
export const deleteDoc = async (
  formId: string
): Promise<{ message: string }> => {
  try {
    const response = await axios.delete(`${DELETE_DOC_URL}${formId}/`, {
      ...AXIOS_CONFIG,
    });

    return {
      message: response.data?.message ?? "Document deleted successfully",
    };
  } catch (error) {
    console.error("Error deleting document:", error);
    throw error;
  }
};

/**
 * Recategorizes a document by its ID.
 *
 * @async
 * @function recategorizeDoc
 * @param {Object} payload - An object containing the document ID, user type, and the new form type.
 * @returns {Promise<{ message: string }>} A promise resolving to a success message.
 * @throws {Error} Will throw if the recategorization fails.
 */
export const recategorizeDoc = async (payload: {
  documentId: string;
  userType: DocumentUserType;
  newForm: string;
}): Promise<Job> => {
  try {
    const response = await axios.post(`${RECATEGORIZE_DOC_URL}`, payload, {
      ...AXIOS_CONFIG,
    });

    return response.data.content as Job;
  } catch (error) {
    console.error("Error recategorizing document:", error);
    throw error;
  }
};

/**
 * Uploads a document to the backend.
 *
 * @async
 * @function uploadDoc
 * @param {Object} payload - An object containing the client ID, tax year, whether it belongs to the spouse, the form type, the file name, and the document data.
 * @returns {Promise<{ message: string , jobId: string}>} A promise resolving to a success message.
 * @throws {Error} Will throw if the upload fails.
 */
export const uploadDoc = async (payload: {
  clientId: string;
  taxYear: number;
  userType: DocumentUserType;
  form: string;
  fileName: string;
  data: string;
}): Promise<{
  message: string;
  jobId: string | null;
  docId: string | null;
  doc: any | null;
}> => {
  try {
    const response = await axios.post(`${UPLOAD_DOC_URL}`, payload, {
      ...AXIOS_CONFIG,
    });

    const doc = response?.data?.content?.doc;

    return {
      message: response.data?.message ?? "Document uploaded successfully",
      jobId: response?.data?.content?.job?.id,
      docId: doc?.docId,
      doc,
    };
  } catch (error) {
    console.error("Error uploading document:", error);
    throw error;
  }
};

/**
 * Unarchives a document by its ID.
 *
 * @param {string} formId - The ID of the form to unarchive.
 * @returns {Promise<{ message: string }>} A promise resolving to a success message.
 */
export const unarchiveDoc = async (
  formId: string
): Promise<{ message: string }> => {
  try {
    const response = await axios.patch(
      `${backend_url}/api/forms/unarchive/${formId}/`,
      {},
      { ...AXIOS_CONFIG }
    );
    return {
      message: response.data?.message ?? "Document unarchived successfully",
    };
  } catch (error) {
    console.error("Error unarchiving document:", error);
    throw error;
  }
};

export const startDocumentOCR = async (documentId: number) => {
  try {
    const response = await axios.post(
      `${backend_url}/api/ocr/document/${documentId}/`,
      {},
      {
        ...AXIOS_CONFIG,
      }
    );

    if (response.status === 200) {
      return response.data.content;
    } else {
      throw new Error("Failed to start OCR job.");
    }
  } catch (error) {
    console.error("Error starting OCR job:", error);
    throw error;
  }
};

export const inviteClientToPortal = async (email: string, message: string) => {
  const response = await axios.post(
    `${backend_url}/api/client/portal-invite/`,
    { email, message },
    { ...AXIOS_CONFIG }
  );
  return response.data;
};

/**
 * Toggles an OCR extraction's enabled/disabled state.
 *
 * @param extractionId - The extraction ID to toggle.
 * @param enabled - Whether to enable or disable the extraction.
 * @returns The updated extraction data.
 */
export const toggleExtraction = async (
  extractionId: number,
  enabled: boolean
): Promise<{ id: number; form: string; title: string; enabled: boolean }> => {
  const response = await axios.patch(
    `${EXTRACTION_TOGGLE_URL}${extractionId}/toggle/`,
    { enabled },
    { ...AXIOS_CONFIG }
  );
  return response.data.content;
};

/**
 * Fetches the full transformed OCR data for a single extraction.
 * Used to lazy-load ocrData that is no longer included in the profile response.
 *
 * @param formId - The document/form ID (docId from DocumentItem)
 * @param extractionId - Optional specific extraction ID. If omitted, returns the latest.
 * @returns The transformed OCR data content.
 */
export const getExtractionOCRData = async (
  formId: string,
  extractionId?: number
): Promise<any> => {
  const params: Record<string, any> = {};
  if (extractionId !== undefined) {
    params.extraction_id = extractionId;
  }
  const response = await axios.get(`${FORM_DATA_URL}${formId}/`, {
    ...AXIOS_CONFIG,
    params,
  });
  return response.data.content;
};
